import { Component, Directive, HostListener, OnInit, ViewChild } from "@angular/core";
import { BasemapDescriptor, Tour, VectorLayerDescriptor } from "./data";
import { Bounds, AnalyticsService } from "map-wald";
import { Observable } from "rxjs";
import { OneTimeSplashComponent } from "./one-time-splash.component";

export interface BaseMapService {
  basemaps$: Observable<BasemapDescriptor[]>;
}

export interface VectorLayerService {
  vectorLayers$: Observable<VectorLayerDescriptor[]>;
}

const LOAD_WMS = 0;
const LOAD_CHOROPLETH = 1;
const LOAD_POINT = 2;
const LOAD_TIME_SERIES = 3;
const LOAD_VECTOR_TILES = 4;

export const MapLoadingIndicators = {
  LOAD_WMS,
  LOAD_CHOROPLETH,
  LOAD_POINT,
  LOAD_TIME_SERIES,
  LOAD_VECTOR_TILES
};

export const MOBILE_TITLE_BAR_STYLES=`
// Mobile view layout
.mobile-container {
  overflow: hidden;
}

// Mobile title bar
.mobile-title-bar {
  border-bottom: 1px solid #e0e0e0;
  min-height: 50px;

  // Logo styling
  a img {
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  // Mobile navigation tabs styling
  .mobile-nav-tabs {
    border-bottom: none;

    .nav-link {
      padding: 0.5rem 0.75rem;
      font-size: 1.1rem;
      border: none;

      color: #ddd;

      i {
        display: block;
      }

      &.active {
        background-color: transparent;
        color: #0d6efd;
        border-bottom: 2px solid #0d6efd;
      }
    }
  }
}
  
::ng-deep .mobile-content-area {
  min-height: 0; // Critical for flex children to shrink properly
  overflow: hidden;

  // Ensure content inside takes full height
  > * {
    height: 100%;
  }

  .tab-pane.active {
    height: 100%;
  }
}

`;

// @Component({
//     selector: 'base-main-map',
//     template: '',
//     styles: []
// })
@Directive()
export abstract class BaseMainMapComponent implements OnInit {
  @ViewChild('splash', { static: true }) splash: OneTimeSplashComponent;

  zoom: number;
  bounds: Bounds;

  showWindows = true;
  basemap: BasemapDescriptor;
  baseMapURL: string;
  
  boundaries = {
    layer: null as VectorLayerDescriptor,
    styles: {} as any
  };

  tour: Tour;

  isMobile: boolean = false;
  private mobileBreakpoint: number = 768; // Example breakpoint
  mobileViewActiveTab = 1;


  processingIndicators:boolean[] = [false,false,false,false,false];
  isProcessing = false;

  constructor(protected baseMapService: BaseMapService, protected vectorLayerService: VectorLayerService, protected analytics: AnalyticsService) {

    this.vectorLayerService.vectorLayers$.subscribe(layers=>{
      this.configureVectorLayer(layers[0]);
    });

    this.baseMapService.basemaps$.subscribe(basemaps=>{
      this.basemap = basemaps[0];
      this.basemapChanged();
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < this.mobileBreakpoint;
    console.log('isMobile',this.isMobile);
  }

  ngOnInit(): void {
    this.checkScreenSize();
  }

  basemapChanged(): void {
    this.baseMapURL = this.basemap.urlTemplate;
  }

  mapBoundsChanged(bounds:Bounds){
    this.bounds = bounds;
  }

  configureVectorLayer(l?: VectorLayerDescriptor): void {
    const target = this.boundaries;
    if(!l){
      l = target.layer;
    }
    if(target.layer && (target.layer.name!==l?.name)){
      this.analytics.analyticsEvent('vector', l.name);
    }

    target.layer = l;
    if(!l||!l.tileLayers){
      return;
    }

    this.configureVectorLayerStyles();
  }

  configureVectorLayerStyles(): void {
    const target = this.boundaries;
    const l = target.layer;
    target.styles = {};
    l.tileLayers.forEach(tl=>{
      target.styles[tl.layer] = (feature:any)=>{
        let result:any = {
          fill:true,
          fillOpacity:0.0,
          ...l.styles||{}
        };
        this.applyCustomVectorStyling(target.layer, result, feature);
        return result;
      }
    });
  }

  applyCustomVectorStyling(layer:VectorLayerDescriptor,result: any, feature: any): void {
  }

  tourStartedFromAbout(tour:Tour): void {
    this.splash.close();
    this.tour = tour;
    this.analytics.analyticsEvent('tour',tour.title);
  }

  vectorTileLoading(loading:boolean): void {
    if(loading){
      this.loading(LOAD_VECTOR_TILES);
    } else {
      this.loaded(LOAD_VECTOR_TILES);
    }
  }

  loading(featureType: number) {
    this.processingIndicators = this.processingIndicators.map((current,i)=>{
      if(i===featureType){
        return true;
      }
      if(i>=LOAD_TIME_SERIES){
        return current;
      }
      if(featureType>=LOAD_TIME_SERIES){
        return current;
      }
      return false;
    })
    this.evalLoading();
  }

  loaded(featureType: number) {
    this.processingIndicators[featureType] = false;
    this.evalLoading();
  }

  evalLoading() {
    setTimeout(()=>{
      this.isProcessing = this.processingIndicators.some(v=>v);
    });
  }

}

